import type { RxCollection, RxConflictHandler } from 'rxdb';
import { deepEqual } from 'rxdb/plugins/utils';
import type {
  DiaryDayBody,
  DiaryDayDocType,
  DiaryExternalSourceSuppression,
  DiaryDayOutboxDocType,
  DiaryMeal,
} from '@macro-tracker/models';
import { normalizeDiaryDayBody } from '@macro-tracker/models';
import { getDeviceModifierId } from '@/services/deviceIdentity';

const mergeArrayDelta = <T>(
  assumed: T[],
  local: T[],
  master: T[],
  keyFor: (item: T) => string,
): T[] => {
  if (deepEqual(assumed, local)) return master;

  const assumedByKey = new Map(
    assumed.map((item) => [keyFor(item), item]),
  );
  const localByKey = new Map(
    local.map((item) => [keyFor(item), item]),
  );
  const removedKeys = new Set(
    assumed
      .map(keyFor)
      .filter((key) => !localByKey.has(key)),
  );
  const changedLocalItems = local.filter((item) => {
    const assumedItem = assumedByKey.get(keyFor(item));
    return assumedItem === undefined || !deepEqual(assumedItem, item);
  });

  const next = master.filter((item) => !removedKeys.has(keyFor(item)));
  const nextIndexByKey = new Map(
    next.map((item, index) => [keyFor(item), index]),
  );
  for (const item of changedLocalItems) {
    const key = keyFor(item);
    const index = nextIndexByKey.get(key);
    if (index === undefined) {
      if (assumedByKey.has(key)) continue;
      nextIndexByKey.set(key, next.length);
      next.push(item);
    } else {
      next[index] = item;
    }
  }
  return next;
};

const suppressionKey = (
  suppression: DiaryExternalSourceSuppression,
): string => [
  suppression.provider,
  suppression.entityType,
  suppression.id,
].join(':');

const hasMeaningfulMealState = (meal: DiaryMeal): boolean => Boolean(
  meal.entries.length > 0
  || meal.completedAt
  || meal.skippedAt
  || meal.deletedAt
  || meal.custom
  || meal.workoutActuals
  || meal.externalSource
  || meal.planned.exercise
  || meal.workoutSupportSnack
  || meal.workoutSupportSnackSuppression,
);

/**
 * Field-level three-way merge of a single meal that changed locally and also
 * exists on master. The meal's own `entries` array gets a nested by-id merge so
 * concurrent logging into the same meal from both sides is preserved; every
 * other field is last-writer-per-field (local wins when it diverged from
 * assumed, otherwise master is kept). Independent creation of a configured
 * slot preserves meaningful user state over generated defaults.
 */
const mergeMealFields = (
  assumed: DiaryMeal | undefined,
  local: DiaryMeal,
  master: DiaryMeal,
): DiaryMeal => {
  const merged: DiaryMeal = {
    ...master,
    entries: mergeArrayDelta(
      assumed?.entries ?? [],
      local.entries,
      master.entries,
      (entry) => entry.id,
    ),
  };
  const independentConfiguredCreation =
    !assumed
    && local.mealId != null
    && local.mealId === master.mealId
    && (local.kind ?? 'meal') === 'meal'
    && (master.kind ?? 'meal') === 'meal';
  if (independentConfiguredCreation) {
    // A stable slot id makes independent generation meet here. Without an
    // assumed meal, generated defaults are not edits to another device's
    // logged, completed or customized meal.
    const localMeaningful = hasMeaningfulMealState(local);
    const masterMeaningful = hasMeaningfulMealState(master);
    if (masterMeaningful && !localMeaningful) return merged;
    if (localMeaningful && !masterMeaningful) return { ...local, entries: merged.entries };
  }
  const keys = new Set<keyof DiaryMeal>([
    ...(Object.keys(assumed ?? {}) as (keyof DiaryMeal)[]),
    ...(Object.keys(local) as (keyof DiaryMeal)[]),
  ]);
  for (const key of keys) {
    if (key === 'entries') continue;
    if (independentConfiguredCreation) {
      const lifecycleKey = key === 'completedAt' || key === 'skippedAt'
        || key === 'deletedAt' || key === 'workoutActuals';
      if (lifecycleKey && local[key] == null) continue;
      if (key === 'planned' && (master.completedAt || master.skippedAt)
        && !local.completedAt && !local.skippedAt) continue;
    }
    if (!deepEqual(assumed?.[key], local[key])) {
      Object.assign(merged, { [key]: local[key] });
    }
  }
  return merged;
};

/**
 * Merge the meals array by `meal.id`. Whole-meal add/remove follows the local
 * delta; a meal changed on both sides is reconciled field-by-field (with a
 * nested entries merge) rather than clobbered wholesale.
 */
const mergeMealsDelta = (
  assumed: DiaryMeal[],
  local: DiaryMeal[],
  master: DiaryMeal[],
): DiaryMeal[] => {
  if (deepEqual(assumed, local)) return master;

  const assumedById = new Map(assumed.map((meal) => [meal.id, meal]));
  const localById = new Map(local.map((meal) => [meal.id, meal]));
  const masterById = new Map(master.map((meal) => [meal.id, meal]));
  const removedIds = new Set(
    assumed.map((meal) => meal.id).filter((id) => !localById.has(id)),
  );

  const result: DiaryMeal[] = [];
  const resultIndexById = new Map<string, number>();
  for (const meal of master) {
    if (removedIds.has(meal.id)) continue;
    resultIndexById.set(meal.id, result.length);
    result.push(meal);
  }

  for (const localMeal of local) {
    const assumedMeal = assumedById.get(localMeal.id);
    if (assumedMeal && deepEqual(assumedMeal, localMeal)) continue; // unchanged locally
    const masterMeal = masterById.get(localMeal.id);
    const index = resultIndexById.get(localMeal.id);
    if (!masterMeal || index === undefined) {
      // Not on master: either newly added locally (keep) or deleted on master (drop).
      if (assumedMeal) continue;
      resultIndexById.set(localMeal.id, result.length);
      result.push(localMeal);
      continue;
    }
    result[index] = mergeMealFields(assumedMeal, localMeal, masterMeal);
  }
  return result;
};

const mergeDiaryBodyDelta = (
  assumed: DiaryDayBody,
  local: DiaryDayBody,
  master: DiaryDayBody,
): DiaryDayBody => {
  const merged: DiaryDayBody = {
    ...master,
    meals: mergeMealsDelta(assumed.meals, local.meals, master.meals),
    externalSourceSuppressions: mergeArrayDelta(
      assumed.externalSourceSuppressions ?? [],
      local.externalSourceSuppressions ?? [],
      master.externalSourceSuppressions ?? [],
      suppressionKey,
    ),
  };

  const scalarKeys: Array<
    Exclude<
      keyof DiaryDayBody,
      'meals' | 'externalSourceSuppressions'
    >
  > = [
    'goalSnapshot',
    'importedTotals',
    'planGeneration',
  ];
  for (const key of scalarKeys) {
    if (!deepEqual(assumed[key], local[key])) {
      Object.assign(merged, { [key]: local[key] });
    }
  }

  return merged;
};

const nextUpdatedAt = (
  localUpdatedAt: string,
  serverUpdatedAt: string,
): string => {
  const parsed = [localUpdatedAt, serverUpdatedAt]
    .map((value) => Date.parse(value))
    .filter(Number.isFinite);
  return new Date(Math.max(Date.now(), ...parsed) + 1).toISOString();
};

const normalizeDiaryDay = <T extends DiaryDayDocType>(day: T): T => {
  const body = normalizeDiaryDayBody(day.body);
  return body === day.body ? day : { ...day, body };
};

export function createDiaryDayConflictHandler(
  getOutbox: () => RxCollection<DiaryDayOutboxDocType>,
): RxConflictHandler<DiaryDayDocType> {
  return {
    isEqual: (left, right) => deepEqual(left, right),
    resolve: async (input) => {
      const id = input.newDocumentState.id;
      const deviceModifierId = await getDeviceModifierId();
      const isAcknowledgedSelfChange =
        input.realMasterState.modifiedBy === deviceModifierId
        && input.realMasterState.syncRevision
          > input.newDocumentState.syncRevision;
      const outbox = await getOutbox().findOne(id).exec();
      if (isAcknowledgedSelfChange) {
        return normalizeDiaryDay(input.realMasterState);
      }
      if (!outbox?.pending) {
        return normalizeDiaryDay(input.realMasterState);
      }

      const assumed = input.assumedMasterState;
      const body = assumed
        ? mergeDiaryBodyDelta(
            assumed.body,
            input.newDocumentState.body,
            input.realMasterState.body,
          )
        : {
            ...input.newDocumentState.body,
            meals: mergeMealsDelta(
              [],
              input.newDocumentState.body.meals,
              input.realMasterState.body.meals,
            ),
          };
      const deletedAt = assumed
        && deepEqual(assumed.deletedAt, input.newDocumentState.deletedAt)
          ? input.realMasterState.deletedAt
          : input.newDocumentState.deletedAt;

      return normalizeDiaryDay({
        ...input.newDocumentState,
        body,
        deletedAt,
        // The server uses syncRevision as a strict CAS token and only accepts
        // the next revision, even when several offline edits were collapsed
        // into this one local document state.
        syncRevision: input.realMasterState.syncRevision + 1,
        modifiedBy: deviceModifierId,
        updatedAt: nextUpdatedAt(
          input.newDocumentState.updatedAt,
          input.realMasterState.updatedAt,
        ),
      });
    },
  };
}
